// attachClipsHelpers.ts
//
// The clip shape and the clip NAME, shared by the two things that write clips:
// `AttachClipsModal` (the clinician picks) and `useAutoAttachClips` (nobody picks).
//
// Lifted out of the modal on 1 Sep when attaching became automatic. Both paths must
// produce byte-identical names: the auto-attached set is what the clinician then sees
// in the modal, and a second naming rule would silently rename every clip the moment
// they opened it.

import { AttachedClip } from '../../../rtk-apis/reports/updateReportClips';

export interface LiteImageItem {
  sopInstanceUID: string;
  url: string;
  thumbnailUrl?: string;
  view?: string;
  instanceNumber?: string;
}

/** True when a DICOM string field carries a real value rather than a placeholder. */
export const isMeaningfulText = (value?: string) => {
  const v = String(value || '').trim();
  return !!v && v.toLowerCase() !== 'na' && v.toLowerCase() !== 'n/a' && v !== '-';
};

/**
 * Label a clip for the shared-clips page: the view name if the cart wrote one,
 * else the instance number, else the tail of the SOP UID, else the position.
 */
export const getDisplayLabel = (item: LiteImageItem, index: number) => {
  if (isMeaningfulText(item.view)) return String(item.view).trim();
  if (isMeaningfulText(item.instanceNumber)) return `Instance ${String(item.instanceNumber).trim()}`;
  const suffix = String(item.sopInstanceUID || '')
    .split('.')
    .filter(Boolean)
    .slice(-2)
    .join('.');
  return suffix ? `Clip ${suffix}` : `Clip ${index + 1}`;
};

/**
 * Every usable lite image as an attachable clip, in the order the study returned them.
 *
 * Items missing a UID or a URL are dropped rather than sent: the backend's
 * `normalizeAttachedClips` would reject the payload, which on the automatic path
 * would mean one bad instance costs the report all of its clips.
 */
export const buildClipsFromLiteImages = (items: LiteImageItem[]): AttachedClip[] =>
  (Array.isArray(items) ? items : [])
    .filter(item => item?.sopInstanceUID && item?.url)
    .map((item, index) => ({
      sopInstanceUID: item.sopInstanceUID,
      url: item.url,
      name: getDisplayLabel(item, index),
    }));
